import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, Clock, Phone, AtSign, Globe, ArrowLeft, Smartphone, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { settingsService, whatsappService } from '../services/api';
import type { BusinessHour, SiteSettings, WhatsAppStatus, WhatsAppQRCode } from '../types/api';
import { WhatsAppQRModal } from './ui';


const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<SiteSettings>({
    contact_email: '',
    whatsapp_number: '',
    whatsapp_display: '',
    business_hours: '',
    timezone: 'America/Sao_Paulo',
    google_analytics_id: '',
    meta_pixel_id: '',
    default_seo_title: '',
    default_seo_description: ''
  });
  const [originalForm, setOriginalForm] = useState<SiteSettings>({
    contact_email: '',
    whatsapp_number: '',
    whatsapp_display: '',
    business_hours: '',
    timezone: 'America/Sao_Paulo',
    google_analytics_id: '',
    meta_pixel_id: '',
    default_seo_title: '',
    default_seo_description: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para WhatsApp
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus | null>(null);
  const [whatsappQR, setWhatsappQR] = useState<WhatsAppQRCode | null>(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingData(true);
        const res = await settingsService.getPublic();
        if (res.success && res.data) {
          // A API retorna um array, pegar o primeiro item
          const settingsData = Array.isArray(res.data) ? res.data[0] : res.data;
          
          // Debug: Log dos dados recebidos
          console.log('Settings data recebido:', settingsData);
          
          // Função para limpar strings com aspas extras
          const cleanString = (str: string | undefined): string => {
            if (!str) return '';
            if (str.startsWith('"') && str.endsWith('"')) {
              return str.slice(1, -1);
            }
            return str;
          };
          
          // Tratar business_hours como string
          const businessHours = cleanString(settingsData.business_hours);
          
          console.log('business_hours limpo:', businessHours);

          const formData: SiteSettings = {
            contact_email: settingsData.contact_email || '',
            whatsapp_number: settingsData.whatsapp_number || '',
            whatsapp_display: settingsData.whatsapp_display || '',
            business_hours: businessHours,
            timezone: settingsData.timezone || 'America/Sao_Paulo',
            google_analytics_id: settingsData.google_analytics_id || '',
            meta_pixel_id: settingsData.meta_pixel_id || '',
            default_seo_title: settingsData.default_seo_title || '',
            default_seo_description: settingsData.default_seo_description || ''
          };
          
          setForm(formData);
          setOriginalForm(formData);
        } else {
          setError(res.error || 'Falha ao carregar configurações');
        }
      } catch (err) {
        setError('Erro ao carregar configurações');
      } finally {
        setLoadingData(false);
      }
    })();
  }, []);

  // Verificar status do WhatsApp
  const checkWhatsAppStatus = async () => {
    try {
      setWhatsappLoading(true);
      const response = await whatsappService.getStatus();
      
      console.log('🔍 Resposta do WhatsApp:', response);
      
      if (response.success && response.data) {
        const data = response.data;
        
        // Verificar se tem QR Code (campos que indicam necessidade de conexão)
        const hasQRCode = !!(data.code || data.base64 || data.pairingCode);
        
        // Verificar se tem instance (indica que está conectado ou tem instância)
        const hasInstance = !!(data.instance);
        
        // Se tem QR Code, está desconectado e precisa conectar
        if (hasQRCode) {
          console.log('📱 WhatsApp desconectado - QR Code disponível');
          console.log('📱 Dados do QR:', data);
          setWhatsappQR(data);
          setWhatsappStatus(null);
        } 
        // Se não tem QR Code mas tem instance, está conectado
        else if (hasInstance) {
          console.log('✅ WhatsApp conectado (tem instance sem QR)');
          // Normalizar a estrutura para garantir compatibilidade
          const statusData: WhatsAppStatus = {
            instance: {
              instanceName: data.instance.instanceName || data.instanceName || 'WhatsApp',
              state: data.instance.state === 'connected' ? 'connected' : 'connected'
            }
          };
          setWhatsappStatus(statusData);
          setWhatsappQR(null);
        }
        // Se não tem QR Code e não tem instance, mas a resposta foi 200, assumir conectado
        // (200 sem QR Code = conectado)
        else {
          console.log('✅ WhatsApp conectado (200 sem QR e sem instance - assumindo conectado)');
          // Criar estrutura padrão para exibir como conectado
          const statusData: WhatsAppStatus = {
            instance: {
              instanceName: 'WhatsApp',
              state: 'connected'
            }
          };
          setWhatsappStatus(statusData);
          setWhatsappQR(null);
        }
      } else {
        console.log('❌ Falha na resposta do WhatsApp');
        setWhatsappStatus(null);
        setWhatsappQR(null);
      }
    } catch (error) {
      console.error('Erro ao verificar status do WhatsApp:', error);
      setWhatsappStatus(null);
      setWhatsappQR(null);
    } finally {
      setWhatsappLoading(false);
    }
  };

  // Verificar status do WhatsApp ao carregar a página
  useEffect(() => {
    checkWhatsAppStatus();
  }, []);

  const setField = (key: keyof SiteSettings, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };


  const handleCancel = () => {
    // Resetar para dados originais
    setForm(originalForm);
    setError('');
    setSuccess('');
    // Voltar para o dashboard
    navigate('/admin');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Sessão expirada. Faça login novamente.');
        setLoading(false);
        return;
      }

      // Debug
      // eslint-disable-next-line no-console
      console.log('Enviando PUT /settings com payload:', {
        contact_email: form.contact_email,
        whatsapp_number: form.whatsapp_number,
        whatsapp_display: form.whatsapp_display,
        business_hours: form.business_hours,
        timezone: form.timezone
      });

      const res = await settingsService.update({
        contact_email: form.contact_email,
        whatsapp_number: form.whatsapp_number,
        whatsapp_display: form.whatsapp_display,
        business_hours: form.business_hours,
        timezone: form.timezone,
        google_analytics_id: form.google_analytics_id,
        meta_pixel_id: form.meta_pixel_id,
        default_seo_title: form.default_seo_title,
        default_seo_description: form.default_seo_description
      });
      // Debug
      // eslint-disable-next-line no-console
      console.log('Resposta PUT /settings:', res);
      if (res.success && res.data) {
        setSuccess('Configurações salvas com sucesso.');
        setForm(prev => ({ ...prev, ...res.data }));
      } else {
        setError(res.error || 'Erro ao salvar.');
      }
    } catch (err: any) {
      setError('Erro de rede ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  const container = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } } };
  const item = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  if (loadingData) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-slate"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section bg-brand-sand pt-32 min-h-screen">
      <div className="wrapper">
        <motion.div initial="hidden" animate="visible" variants={container} className="max-w-3xl mx-auto">
          <motion.div variants={item} className="text-center mb-10">
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-brand-slate hover:text-brand-accent transition-colors duration-200"
              >
                <ArrowLeft size={20} />
                <span>Voltar ao Dashboard</span>
              </button>
            </div>
            
            <h1 className="text-4xl font-heading text-brand-slate">Configurações do Site</h1>
            <p className="text-brand-ink/70 mt-2">Atualize informações gerais usadas em todo o site</p>
          </motion.div>

          <form onSubmit={handleSubmit} className="card card-pad space-y-8">
            {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-3 text-sm">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

            {/* E-mail de contato */}
            <motion.div variants={item} className="space-y-3">
              <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                <AtSign className="w-4 h-4" />
                E-mail de contato
              </label>
              <input 
                type="email" 
                required 
                className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent" 
                value={form.contact_email} 
                onChange={e => setField('contact_email', e.target.value)} 
                placeholder="contato@dominio.com" 
              />
            </motion.div>

            {/* WhatsApp */}
            <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  WhatsApp (callback)
                </label>
                <input 
                  type="text" 
                  required 
                  className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent" 
                  value={form.whatsapp_number} 
                  onChange={e => setField('whatsapp_number', e.target.value)} 
                  placeholder="5511999999999" 
                />
                <p className="text-xs text-brand-ink/60">Somente dígitos, incluindo DDI e DDD</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  WhatsApp (exibição)
                </label>
                <input 
                  type="text" 
                  required 
                  className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent" 
                  value={form.whatsapp_display} 
                  onChange={e => setField('whatsapp_display', e.target.value)} 
                  placeholder="+55 (11) 99999-9999" 
                />
              </div>
            </motion.div>

            {/* Separador */}
            <div className="w-full h-px bg-brand-slate/10 my-4 pt-1"></div>
            <h3 className="text-xl font-heading text-brand-slate">SEO & Rastreamento Global</h3>
            <p className="text-sm text-brand-ink/70 -mt-6">Injete IDs de tráfego e configure a leitura padrão do Google (Apenas códigos. Não insira HTML).</p>

            <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="text-sm font-medium text-brand-slate">
                  ID do Google Analytics (ex: G-XXXX)
                </label>
                <input 
                  type="text" 
                  className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 bg-transparent" 
                  value={form.google_analytics_id || ''} 
                  onChange={e => setField('google_analytics_id', e.target.value)} 
                  placeholder="G-..." 
                />
              </div>
              <div className="space-y-3">
                <label className="text-sm font-medium text-brand-slate">
                  ID do Meta Pixel (Facebook)
                </label>
                <input 
                  type="text" 
                  className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 bg-transparent" 
                  value={form.meta_pixel_id || ''} 
                  onChange={e => setField('meta_pixel_id', e.target.value)} 
                  placeholder="1234567890" 
                />
              </div>
            </motion.div>

            <motion.div variants={item} className="space-y-3">
              <label className="text-sm font-medium text-brand-slate">
                Título SEO Padrão (Exibido no Google)
              </label>
              <input 
                type="text" 
                className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 bg-transparent" 
                value={form.default_seo_title || ''} 
                onChange={e => setField('default_seo_title', e.target.value)} 
                placeholder="Luara Rezende | Advocacia Especializada" 
              />
            </motion.div>

            <motion.div variants={item} className="space-y-3">
              <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                Descrição SEO Padrão (Meta Desc)
              </label>
              <textarea 
                className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 bg-transparent resize-none" 
                value={form.default_seo_description || ''} 
                onChange={e => setField('default_seo_description', e.target.value)} 
                placeholder="Descrição curta de até 160 caracteres."
                rows={2}
              />
            </motion.div>

            <div className="w-full h-px bg-brand-slate/10 my-4 pt-1"></div>
            <h3 className="text-xl font-heading text-brand-slate">Serviços Locais</h3>

            {/* Fuso horário */}
            <motion.div variants={item} className="space-y-3">
              <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Fuso horário
              </label>
              <input 
                type="text" 
                className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent" 
                value={form.timezone} 
                onChange={e => setField('timezone', e.target.value)} 
                placeholder="America/Sao_Paulo" 
              />
            </motion.div>

            {/* Horário de atendimento */}
            <motion.div variants={item} className="space-y-3">
              <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Horário de atendimento
              </label>
              <textarea 
                className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent resize-none" 
                value={form.business_hours} 
                onChange={e => setField('business_hours', e.target.value)} 
                placeholder="Ex: Segunda a sexta: 8h às 18h&#10;Sábado: 8h às 12h&#10;Domingo: Fechado"
                rows={3}
              />
              <p className="text-xs text-brand-ink/60">Descreva os horários de atendimento em texto livre</p>
            </motion.div>

            {/* Conexão WhatsApp */}
            <motion.div variants={item} className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Conexão WhatsApp
                </label>
                <button
                  type="button"
                  onClick={checkWhatsAppStatus}
                  disabled={whatsappLoading}
                  className="btn-secondary text-xs px-3 py-2 inline-flex items-center gap-2"
                >
                  <RefreshCw className={`w-3 h-3 ${whatsappLoading ? 'animate-spin' : ''}`} />
                  Atualizar
                </button>
              </div>
              
              <div className="bg-brand-sand/30 rounded-xl p-4">
                {/* Debug info - remover depois */}
                <div className="text-xs text-brand-ink/40 mb-2">
                  Debug: loading={whatsappLoading ? 'true' : 'false'}, 
                  status={whatsappStatus ? 'true' : 'false'}, 
                  qr={whatsappQR ? 'true' : 'false'}
                </div>
                
                {whatsappLoading ? (
                  <div className="flex items-center gap-3 text-brand-ink/70">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Verificando status...</span>
                  </div>
                ) : whatsappStatus ? (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-green-700">
                        WhatsApp Conectado
                      </p>
                      <p className="text-xs text-brand-ink/60">
                        Instância: {whatsappStatus.instance.instanceName}
                      </p>
                    </div>
                  </div>
                ) : whatsappQR ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-orange-700">
                          WhatsApp Desconectado
                        </p>
                        <p className="text-xs text-brand-ink/60">
                          Conecte seu WhatsApp para enviar mensagens
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🔍 Clicou em conectar WhatsApp, whatsappQR:', whatsappQR);
                        console.log('🔍 Abrindo modal...');
                        console.log('🔍 Estado atual showQRModal:', showQRModal);
                        setShowQRModal(true);
                        console.log('🔍 Estado após setShowQRModal(true)');
                      }}
                      className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Conectar WhatsApp
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-brand-ink/70">
                      <WifiOff className="w-4 h-4" />
                      <span className="text-sm">Status desconhecido</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🔍 Clicou em conectar WhatsApp (status desconhecido)');
                        setShowQRModal(true);
                      }}
                      className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Tentar Conectar
                    </button>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div variants={item} className="pt-2 flex gap-4">
              <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> {loading ? 'Salvando...' : 'Salvar alterações'}
              </button>
              
              <button 
                type="button" 
                onClick={handleCancel}
                className="btn-secondary inline-flex items-center gap-2"
              >
                Cancelar
              </button>
            </motion.div>
          </form>
        </motion.div>
      </div>
      
      {/* Modal do QR Code */}
      <WhatsAppQRModal
        isOpen={showQRModal}
        onClose={() => {
          console.log('🔍 Fechando modal');
          setShowQRModal(false);
        }}
        qrData={whatsappQR}
      />
    </section>
  );
};

export default AdminSettings;


